import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DAY_LABELS, firstOfMonth, daysInMonth, ymd } from "./utils";

export default function JournalCalendar({ year, month, byDate, selectedDate, onSelectDate, onGotoMonth }) {
  const today = new Date();
  const first = firstOfMonth(year, month);
  const totalDays = daysInMonth(year, month);
  // JS: 0=Sun, 1=Mon ... shift so Monday=0
  const firstWeekdayMondayStart = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekdayMondayStart; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = new Date(year, month, 1).toLocaleString("en", { month: "long", year: "numeric" });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="journal-prev-month"
          onClick={() => onGotoMonth(-1)}
          className="w-9 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 grid place-items-center"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-slate-500">Journal</div>
          <div className="mt-0.5 font-display font-bold text-xl md:text-2xl tracking-tight" data-testid="journal-month-label">{monthLabel}</div>
        </div>
        <button
          data-testid="journal-next-month"
          onClick={() => onGotoMonth(1)}
          className="w-9 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 grid place-items-center"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400 text-center">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`empty-${year}-${month}-${idx}`} />;
          const key = ymd(new Date(year, month, day));
          const data = byDate[key];
          const trade = data?.trade || 0;
          const hasDeposit = data?.deposit > 0;
          const hasWithdraw = data?.withdraw > 0;
          const isSelected = key === selectedDate;
          const isToday = key === ymd(today);
          const tradeCls =
            trade > 0 ? "bg-emerald-500 text-white" :
            trade < 0 ? "bg-rose-500 text-white" :
            data ? "bg-slate-100 text-slate-700" : "bg-white text-slate-700";
          return (
            <button
              key={key}
              data-testid={`journal-day-${key}`}
              onClick={() => onSelectDate(key)}
              className={`aspect-square rounded-lg border flex flex-col items-center justify-center relative
                ${isSelected ? "border-[#00b4ff] ring-2 ring-[#00b4ff]/30" : "border-slate-200 hover:border-slate-300"}
                ${data ? tradeCls : "hover:bg-slate-50"}
              `}
              style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}
            >
              <div className={`text-sm font-display font-semibold ${isToday && !data ? "text-[#00b4ff]" : ""}`}>{day}</div>
              {trade !== 0 && (
                <div className="text-[9px] font-mono-plex font-semibold tracking-wider mt-0.5">
                  {trade > 0 ? "+" : "-"}${Math.abs(trade).toFixed(Math.abs(trade) >= 100 ? 0 : 1)}
                </div>
              )}
              {(hasDeposit || hasWithdraw) && (
                <div className="absolute top-1 right-1 flex gap-0.5">
                  {hasDeposit && <span className="w-1.5 h-1.5 rounded-full bg-[#00b4ff]" title="Deposit" />}
                  {hasWithdraw && <span className="w-1.5 h-1.5 rounded-full bg-slate-600" title="Withdraw" />}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Profit</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> Loss</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#00b4ff]" /> Deposit</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-600" /> Withdraw</span>
      </div>
    </div>
  );
}
